/**
 * The platform's rounds, read back onto the boards that walk them: the
 * airfield inspection board, the Inspection platform's three scopes
 * (airfield, buildings and landside — the last two from build 7) and the
 * Systems board's daily utility checks (build 7). See mergeInspections.ts.
 *
 * Renders nothing and sits in the shell. Only on the platform: under the mock
 * the board keeps the rounds walked in this browser, exactly as it always has.
 * It asks for nothing until the legacy engine — the only reader of what it
 * fills — has loaded, and it never causes that load; the merge (and the tables
 * it reads) is imported only when there is something to merge.
 *
 * A refusal is an answer: the register is emptied of everything but this
 * browser's own rounds and the board says why. Any other failure leaves the
 * board as it was — the last list stands until the platform answers again.
 *
 * The utility checks are their own query: thirteen systems checked daily
 * outgrow the area rounds' hundred within a week, and the board reads thirty
 * days back.
 */
import { useEffect, useState } from "react";
import { useSession } from "../../../app/SessionContext";
import { DataSourceError } from "../../../api/dataSource";
import { useInspectionRounds } from "../../../api/inspectionsQueries";
import { FIELD_ROUND } from "../../../domain/inspectionRounds";
import { BUILDING_ROUND, LANDSIDE_ROUND, UTILITY_CHECK } from "../../../domain/boardChecklists";
import { demoIfLoaded, whenDemoLoaded } from "../../domain/load";

const TEMPLATES = [FIELD_ROUND, BUILDING_ROUND, LANDSIDE_ROUND] as const;
const UTILITY = [UTILITY_CHECK] as const;
const merger = () => import("../mergeInspections");

export function InspectionsFeed() {
  const { source } = useSession();
  const live = source.kind === "platform";
  const [engine, setEngine] = useState(() => demoIfLoaded() !== null);
  useEffect(() => {
    if (!engine) whenDemoLoaded(() => setEngine(true));
  }, [engine]);

  const rounds = useInspectionRounds(TEMPLATES, { enabled: live && engine });
  const checks = useInspectionRounds(UTILITY, { enabled: live && engine, limit: 500 });
  const data = rounds.data;
  const error = rounds.error;

  useEffect(() => {
    if (!live) return;
    if (error instanceof DataSourceError && error.status === 403) {
      const why = error.message;
      whenDemoLoaded(() => void merger().then((m) => m.mergeRounds([], why)));
      return;
    }
    if (!data) return;
    whenDemoLoaded(() => void merger().then((m) => m.mergeRounds(data)));
  }, [live, data, error]);

  const checkData = checks.data;
  const checkRefused = checks.error instanceof DataSourceError && checks.error.status === 403;
  useEffect(() => {
    if (!live) return;
    if (checkRefused) {
      whenDemoLoaded(() => void merger().then((m) => m.mergeUtilityChecks([])));
      return;
    }
    if (!checkData) return;
    whenDemoLoaded(() => void merger().then((m) => m.mergeUtilityChecks(checkData)));
  }, [live, checkData, checkRefused]);

  return null;
}
